import { useState } from "react";
import { format } from "date-fns";
import { Clock, Camera, Eye, Info } from "lucide-react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDeviceViewCount } from "@/hooks/use-device-view-count";
import { useIsMobile } from "@/hooks/use-mobile";
import { getImageFreshness } from "@/utils/timeFreshnessUtils";
import { MiniMapDialog } from "./MiniMapDialog";

interface PublicDeviceTileProps {
  device: {
    device_id: string;
    device_name: string;
    device_description?: string | null;
    device_last_updated: string;
    lat_wgs84: number | null;
    long_wgs84: number | null;
    past_images?: any[];
    imageKit?: {
      thumbnailUrl?: string;
      fullUrl?: string;
    };
  };
  organizationId: string;
}

export const PublicDeviceTile = ({ device, organizationId }: PublicDeviceTileProps) => {
  const { data: viewCount = 0 } = useDeviceViewCount(device.device_id);
  const isMobile = useIsMobile();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  console.log('🎥 [PublicDeviceTile] Rendering device:', device.device_name, {
    device_id: device.device_id,
    hasImageKit: !!device.imageKit,
    imageKitUrl: device.imageKit?.thumbnailUrl,
    hasPastImages: !!device.past_images?.length,
    pastImagesCount: device.past_images?.length,
    viewCount,
    hasDescription: !!device.device_description,
    descriptionLength: device.device_description?.length
  });
  
  try {
    const lastUpdatedDate = device.device_last_updated ? new Date(device.device_last_updated) : null;
    const lastUpdatedText = lastUpdatedDate && !isNaN(lastUpdatedDate.getTime())
      ? format(lastUpdatedDate, "MMM d, yyyy 'at' h:mm a")
      : 'Unknown';

    // Get latest image from past_images
    const latestImage = device.past_images && device.past_images.length > 0 
      ? device.past_images[device.past_images.length - 1] as any
      : null;

    const thumbnailUrl = device.imageKit?.thumbnailUrl || latestImage?.url;
    const fullImageUrl = device.imageKit?.fullUrl || latestImage?.url || thumbnailUrl;
    
    // Get freshness status
    const freshness = getImageFreshness(device.device_last_updated);
    
    console.log('🎥 [PublicDeviceTile] Computed values:', {
      lastUpdatedText,
      hasThumbnailUrl: !!thumbnailUrl,
      thumbnailUrl,
      freshness
    });

  return (
    <div className="showcase-device-card group relative rounded-lg border bg-contact-button hover:shadow-lg transition-all duration-300 overflow-hidden">
        {/* Image Section */}
        <div className="relative p-3 pb-2">
          {thumbnailUrl ? (
            <div className="relative">
              <Zoom zoomMargin={40} classDialog="z-[100]">
                <img
                  src={fullImageUrl}
                  alt={device.device_name}
                  className="w-full aspect-square object-cover rounded-md cursor-zoom-in"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </Zoom>
              
              {/* Info Icon with Description - Tooltip for Desktop, Dialog for Mobile */}
              {device.device_description && device.device_description.trim() && (
                <>
                  {isMobile ? (
                    <>
                      <button 
                        type="button"
                        onClick={() => setIsDialogOpen(true)}
                        className="showcase-image-action absolute top-2 left-2 z-50 bg-foreground/70 text-background p-1.5 rounded-full hover:bg-foreground/90 transition-colors shadow-lg"
                        aria-label="View device description"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogContent className="showcase-dialog max-w-[90vw] sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>{device.device_name}</DialogTitle>
                          </DialogHeader>
                          <div className="text-sm text-muted-foreground">
                            {device.device_description}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  ) : (
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button 
                          type="button"
                          className="showcase-image-action absolute top-2 left-2 z-50 bg-foreground/70 text-background p-1.5 rounded-full hover:bg-foreground/90 transition-colors shadow-lg"
                          aria-label="View device description"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-sm">{device.device_description}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}

              {/* Freshness Badge Overlay */}
              <div className="absolute top-2 right-2 z-40 pointer-events-none">
                <Badge 
                  variant={freshness.variant}
                  className={`${freshness.colorClass} shadow-md text-destructive-foreground border-0`}
                >
                  {freshness.text}
                </Badge>
              </div>

              {/* Mini-Map Icon - only show if coordinates exist */}
              {device.lat_wgs84 && device.long_wgs84 && (
                <MiniMapDialog
                  latitude={device.lat_wgs84}
                  longitude={device.long_wgs84}
                  deviceName={device.device_name}
                  thumbnailUrl={thumbnailUrl}
                />
              )}

              {/* View Count Badge */}
              {viewCount > 0 && (
                <div className="absolute bottom-2 right-2 z-40 pointer-events-none">
                  <Badge variant="secondary" className="showcase-image-badge bg-foreground/70 text-background border-0 shadow-md">
                    <Eye className="h-3 w-3 mr-1" />
                    {viewCount.toLocaleString()}
                  </Badge>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full aspect-square bg-muted rounded-md flex flex-col items-center justify-center gap-2">
              <Camera className="h-12 w-12 text-muted-foreground/40" />
              <span className="text-sm text-muted-foreground">No image available</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="px-3 pb-3 space-y-2">
          {/* Device Name - White Background */}
          <div className="showcase-device-name bg-background rounded-lg p-3">
            <h3 className="showcase-heading font-bold text-sm leading-tight truncate">
              {device.device_name || "Unnamed Device"}
            </h3>
          </div>

          {/* Last Updated - Centered on tile background */}
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground py-1">
            <Clock className="h-3 w-3" />
            <span>{lastUpdatedText}</span>
          </div>
        </div>
      </div>
  );
  } catch (error) {
    console.error('🎥 [PublicDeviceTile] Render error:', error, device);
    throw error;
  }
};
